import { DataTable } from '@/components/features/shared/DataTable'
import DateSelector from '@/components/features/shared/DateSelector'
import { NavHeader } from '@/components/features/shared/NavHeader'
import { SummaryExtended } from '@/components/features/shared/SummaryExtended'
import { downloadCsv, formatCurrency, formatNumEsAr, PriceLabel, PriceLabelSimple, renderDate } from '@/components/features/shared/utils'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Label } from '@/components/ui/label'
import { AgreementStatus, formatAgreementStatus, getAgreementStatusColor } from '@/domain/agreements/agreement'
import { SaleByOffering } from '@/domain/sellers/sellers'
import { useSalesBySeller, useSeller } from '@/hooks/api/useSellers'
import { useDateRange } from '@/hooks/useDateRange'
import { Paths } from '@/routes/paths'
import { formatDocNumber } from '@/utils/helpers'
import { useNavigate, useParams } from '@tanstack/react-router'
import { ColumnDef } from '@tanstack/react-table'
import { endOfDay, format, startOfDay } from 'date-fns'
import { ArrowRight, DollarSign, TrendingUp } from 'lucide-react'
import { useMemo } from 'react'
import { PageContainer } from '@/components/features/shared/PageContainer'

export function SalesBySellerView() {
  const { sellerId } = useParams({ strict: false }) as { sellerId: string }
  const navigate = useNavigate()

  const now = new Date()
  const { fromDate, toDate, setFromDate, setToDate } = useDateRange({
    fromDate: startOfDay(now),
    toDate: endOfDay(now),
  })

  const handleSearch = () => {
    navigate({
      search: (old: any) => ({ ...old, page: 1 }),
      replace: true,
    } as any)
  }

  const handleExportCsv = () => {
    if (contracts.length === 0) return

    const headers = ['Nro Contrato',
      'Cliente',
      'Documento',
      'Servicio',
      'Importe',
      'Inicio',
      'Fin',
      'Estado',
      'Comision %',
      'Total Comision'
    ]

    const rows = contracts.map(c => {
      return [
        c.contractNumber,
        `"${c.clientCode} - ${c.clientName}"`,
        `"${c.clientDocType} ${formatDocNumber(c.clientDocNumber, c.clientDocType)}"`,
        `"${c.offeringCode} - ${c.offeringName}"`,
        `"$ ${formatNumEsAr(c.amount)}"`,
        format(c.startDate, 'dd/MM/yyyy'),
        c.endDate ? format(c.endDate, 'dd/MM/yyyy') : '-',
        formatAgreementStatus(c.status as AgreementStatus),
        `${c.sellerCommission}%`,
        `"$ ${formatNumEsAr(c.amount * c.sellerCommission / 100)}"`].join(';')
    })

    const totalCommission = contracts.reduce((sum, c) => sum + c.amount * c.sellerCommission / 100, 0)
    const totalRow = [...Array(headers.length - 1).fill(''), formatNumEsAr(totalCommission)].join(';')
    const csvContent = [headers.join(';'), ...rows, totalRow].join('\n')
    const filename = `contratos_vendedor_${seller?.code}_${fromDate ? format(fromDate, 'dd-MM-yyyy') : ''}_${toDate ? format(toDate, 'dd-MM-yyyy') : ''}.csv`
    downloadCsv(csvContent, filename)
  }

  const { data: seller } = useSeller(sellerId)
  const { data: contracts = [], isLoading } = useSalesBySeller(
    sellerId,
    fromDate!,
    toDate!,
    { enabled: true }
  )

  const columns: ColumnDef<SaleByOffering>[] = useMemo(() => [
    {
      accessorKey: 'contractNumber',
      header: 'Nro Contrato',
      cell: ({ row }) => (
        <span className="font-mono font-medium">{row.original.contractNumber}</span>
      ),
    },
    {
      accessorKey: 'clientName',
      header: 'Cliente',
      cell: ({ row }) => (
        <div className="flex flex-col">
          <span className="font-medium">{row.original.clientName}</span>
          <span className="text-xs text-muted-foreground">
            Codigo: {row.original.clientCode} · {row.original.clientDocType} {formatDocNumber(row.original.clientDocNumber, row.original.clientDocType)}
          </span>
        </div>
      ),
    },
    {
      accessorKey: 'offeringName',
      header: 'Servicio',
      cell: ({ row }) => (
        <div className="flex flex-col">
          <span>{row.original.offeringCode} - {row.original.offeringName}</span>
        </div>
      ),
    },
    {
      accessorKey: 'amount',
      header: 'Importe',
      cell: ({ row }) => (
        <PriceLabelSimple price={row.original.amount} />
      ),
    },
    {
      accessorKey: 'startDate',
      header: 'Inicio',
      cell: ({ row }) => (
        renderDate(row.original.startDate)
      ),
    },
    {
      accessorKey: 'endDate',
      header: 'Fin',
      cell: ({ row }) => (
        renderDate(row.original.endDate)
      ),
    },
    {
      accessorKey: 'status',
      header: 'Estado contrato',
      cell: ({ row }) => {
        const status = row.original.status as AgreementStatus
        return (
          <Badge className={getAgreementStatusColor(status)}>
            {formatAgreementStatus(status)}
          </Badge>
        )
      },
    },
    {
      accessorKey: 'sellerCommission',
      header: 'Comisión %',
      cell: ({ row }) => (
        <div className="text-sm">{row.original.sellerCommission}%</div>
      ),
    },
    {
      accessorKey: 'totalCommision',
      header: 'Total comisión',
      cell: ({ row }) => (
        <PriceLabel price={row.original.amount * row.original.sellerCommission / 100} />
      ),
    },
    {
      id: 'acciones',
      header: 'Acciones',
      cell: ({ row }) => (
        <Button
          variant="outline"
          size="sm"
          onClick={(e) => {
            e.stopPropagation()
            navigate({ to: `${Paths.Management.Contracts}/${row.original.contractId}` })
          }}
          title='Ver contrato'
        >
          <ArrowRight className="h-4 w-4" />
        </Button>
      ),
    },
  ], [])

  return (
    <PageContainer>
      <NavHeader>
        <div>
          <h1 className="text-3xl font-bold tracking-tight">Contratos del Vendedor N° {seller?.code}</h1>
          <p className="text-muted-foreground">
            Período: {format(fromDate!, 'dd/MM/yyyy')} a {format(toDate!, 'dd/MM/yyyy')}
          </p>
        </div>
      </NavHeader>

      <Card>
        <CardContent className="pt-6">
          <div className="flex items-end gap-4 flex-wrap">
            <div className="flex flex-col gap-1">
              <Label className="text-xs">Desde</Label>
              <DateSelector initialDate={fromDate} onDateSelected={setFromDate} />
            </div>
            <div className="flex flex-col gap-1">
              <Label className="text-xs">Hasta</Label>
              <DateSelector initialDate={toDate} onDateSelected={setToDate} />
            </div>
            <div className="flex flex-col ml-auto gap-1">
              <Label className="text-xs">&nbsp;</Label>
              <Button variant="info" onClick={handleSearch}>
                Buscar
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>

      <SummaryExtended
        items={[
          {
            title: 'Total Vendido',
            value: formatCurrency(contracts.reduce((sum, c) => sum + c.amount, 0)),
            icon: <DollarSign className="h-4 w-4 text-muted-foreground" />,
            subtitle: `${format(fromDate!, 'dd/MM/yyyy')} al ${format(toDate!, 'dd/MM/yyyy')}`,
          },
          {
            title: 'Contratos',
            value: contracts.length,
            icon: <TrendingUp className="h-4 w-4 text-blue-600" />,
            subtitle: 'Total en período',
          },
          {
            title: 'Comisiones',
            value: formatCurrency(contracts.reduce((sum, c) => sum + c.amount * c.sellerCommission / 100, 0)),
            icon: <DollarSign className="h-4 w-4 text-emerald-600" />,
            subtitle: 'Total a pagar',
            valueClassName: 'text-emerald-600',
          },
        ]}
      />

      <Card>
        <CardHeader className="flex flex-row items-center justify-between gap-4">
          <div className="space-y-1">
            <CardTitle>Contratos vendidos por {seller?.name}</CardTitle>
            <CardDescription>
              Detalle de contratos vendidos
            </CardDescription>
          </div>
          <Button variant="outline" onClick={handleExportCsv} disabled={contracts.length === 0}>
            Exportar CSV
          </Button>
        </CardHeader>
        <CardContent>
          {isLoading ? (
            <div className="flex items-center justify-center py-12">
              <p className="text-muted-foreground">Cargando contratos...</p>
            </div>
          ) : (
            <DataTable
              columns={columns}
              data={contracts}
            />
          )}
        </CardContent>
      </Card>
    </PageContainer>
  )
}
